import React from 'react';
import classNames from 'classnames';
import { AnchorId } from '../../core/Enums';
import type {
  MaintenanceRequest,
  MaintenanceRequestStatus,
} from '../../core/Types';
import { useTourAnchor } from '../../hooks/useTourAnchor';
import { getRequestNumber, getVendorLabel } from './Utils';
import PriorityPill from './PriorityPill';
import StatusPill from './StatusPill';
import rowStyles from '../../stylesheets/maintenance/requestRow.module.scss';

interface RequestRowProps {
  request: MaintenanceRequest;
  isSpotlightTarget?: boolean;
  onSelect: (requestId: string) => void;
}

const ROW_STATUS_LABELS: Record<MaintenanceRequestStatus, string> = {
  new: 'Pending',
  open: 'Open',
  inProgress: 'In Progress',
  completed: 'Completed',
};

const RequestRow = ({
  request,
  isSpotlightTarget = false,
  onSelect,
}: RequestRowProps) => {
  const vendorLabel = getVendorLabel(request.vendor);
  const isUnassigned = vendorLabel === 'Unassigned';
  const requestNumber = getRequestNumber(request.id);
  const rowStatusLabel = ROW_STATUS_LABELS[request.status];
  const unitLabel = request.unitName.replace(/^Unit\s+/i, '').trim();
  const siteLabel = `${request.propertyName} ${unitLabel}`.trim();
  const mobileVendorLabel = isUnassigned ? '' : vendorLabel;
  const mobileStatusClass = {
    new: rowStyles.mobileStatusNew,
    open: rowStyles.mobileStatusOpen,
    inProgress: rowStyles.mobileStatusInProgress,
    completed: rowStyles.mobileStatusCompleted,
  }[request.status];
  const requestRowRef = useTourAnchor(AnchorId.MAINTENANCE_REQUEST_ROW);

  return (
    <button
      type="button"
      className={rowStyles.requestRow}
      onClick={() => onSelect(request.id)}
      ref={isSpotlightTarget ? requestRowRef : undefined}
      data-tour={
        isSpotlightTarget ? AnchorId.MAINTENANCE_REQUEST_ROW : undefined
      }
    >
      <div className={rowStyles.mobileRow}>
        <div
          className={classNames(rowStyles.mobileCell, rowStyles.mobileNumber)}
        >
          <span className={rowStyles.mobileCellLabel}>Number</span>
          <span className={rowStyles.mobileCellValue}>{requestNumber}</span>
        </div>
        <div className={classNames(rowStyles.mobileCell, rowStyles.mobileDate)}>
          <span className={rowStyles.mobileCellLabel}>Date</span>
          <span className={rowStyles.mobileCellValue}>
            {request.requestedAt}
          </span>
        </div>
        <div
          className={classNames(rowStyles.mobileCell, rowStyles.mobileVendor)}
        >
          <span className={rowStyles.mobileCellLabel}>Vendor</span>
          <span className={rowStyles.mobileCellValue}>
            {mobileVendorLabel || '-'}
          </span>
        </div>
        <div className={classNames(rowStyles.mobileCell, rowStyles.mobileSite)}>
          <span className={rowStyles.mobileCellLabel}>Site</span>
          <span className={rowStyles.mobileCellValue}>{siteLabel}</span>
        </div>
        <div
          className={classNames(rowStyles.mobileCell, rowStyles.mobileCategory)}
        >
          <span className={rowStyles.mobileCellLabel}>Category</span>
          <span
            className={classNames(
              rowStyles.mobileCellValue,
              rowStyles.mobileValueBold
            )}
          >
            {request.category}
          </span>
        </div>
        <div
          className={classNames(rowStyles.mobileCell, rowStyles.mobileStatus)}
        >
          <span className={rowStyles.mobileCellLabel}>Status</span>
          <span
            className={classNames(
              rowStyles.mobileStatusValue,
              mobileStatusClass
            )}
          >
            {rowStatusLabel}
          </span>
        </div>
      </div>
      <div className={rowStyles.requestPrimary}>
        <div className={rowStyles.requestTitleRow}>
          <span className={rowStyles.requestTitle}>{request.title}</span>
          <span className={rowStyles.requestId}>{request.id}</span>
        </div>
        <div className={rowStyles.requestMeta}>
          {request.unitName} | {request.propertyName}
        </div>
        <div className={rowStyles.requestCategory}>{request.category}</div>
      </div>
      <div className={rowStyles.requestStatus}>
        <StatusPill status={request.status} />
        <PriorityPill priority={request.priority} />
      </div>
      <div className={rowStyles.requestVendor}>
        <span
          className={classNames(
            rowStyles.vendorPill,
            isUnassigned ? rowStyles.vendorUnassigned : rowStyles.vendorAssigned
          )}
        >
          {vendorLabel}
        </span>
        <span className={rowStyles.vendorCaption}>
          {isUnassigned ? 'Needs assignment' : 'Assigned'}
        </span>
      </div>
      <div className={rowStyles.requestDate}>{request.requestedAt}</div>
    </button>
  );
};

export default RequestRow;
